/**
 * OI polygons (mockup `drawOIPolygons` + `_focusOIPolygon`).
 *
 * Reads real geometry from the OI list REST cache (`['oi','list']`).
 * Falls back to a 0.025° box around each drone's last known position
 * for OIs that haven't loaded yet — this avoids an empty map flash on
 * first render.
 *
 * Visibility rules (mockup parity):
 *   - Drones-ctx + `showOIVolume` ON → render all OIs assigned to fleet drones
 *   - OI-ctx                          → render all OIs matching status filters
 *
 * Stroke style is per-status; hovered/selected polygon gets a 4 px stroke
 * (driven by `useOiStore.hoveredId / selectedId`).
 */
import mapboxgl from 'mapbox-gl';
import type { GeoJSONSource, Map as MapboxMap } from 'mapbox-gl';
import type { Feature, Polygon } from 'geojson';
import { droneFilterKey, visibleFleet } from '@/lib/map/droneFilter';
import { useDronesStore } from '@/lib/store/drones';
import { useUiStore } from '@/lib/store/ui';
import { useOiStore, type OiStatusFilter } from '@/lib/store/oi';
import { queryClient } from '@/lib/query';
import { getCurrentExternalOis } from '@/lib/api/queries';
import { composeOis } from '@/lib/oi/mergeOis';
import { filterExternalOis } from '@/lib/oi/externalFilter';
import { isFutureOi } from '@/lib/oi/oiTime';
import { searchAreaDragJustEnded } from './oiSearchArea';
// Polygon colour is the OI's RTM status — `rtm_state`, straight from the
// backend (ENG-213). Nothing here derives conformance from the drone's
// position; the prediction diamond (markers/predProjection.ts) is separate.
import type { OiSummary } from '@/lib/api/types';
import {
  VOLUME_DASH,
  VOLUME_LINE_WIDTH,
  VOLUME_LINE_WIDTH_FOCUSED,
  VOLUME_PALETTE,
} from '@/lib/map/volumeStyle';

const SRC = 'src-oi';
const FILL_LAYER = 'lyr-oi-fill';
const LINE_LAYER = 'lyr-oi-line';

// Stale OIs (telemetry lost > 10s, doc 007 OI Unknown branch) render in
// neutral gray regardless of last-known status — the polygon should not
// assert green/amber/red confidence about a drone we can't see.
// Only some features carry these flags. A bare `['get', …]` yields null on
// the rest, and a non-boolean `case` condition fails the whole expression —
// for line-dasharray that crashes the tile worker. Compare explicitly.
const IS_PARTNER = ['==', ['get', 'partner'], true];
const IS_THIRDPARTY = ['==', ['get', 'thirdparty'], true];
// ENG-191 — OIs whose window hasn't opened yet render faded.
const IS_FUTURE = ['==', ['get', 'future'], true];

// Partner OIs take the tenant accent regardless of status (mockup
// `_drawOITimelineOIs` external loop: `color = oi.tenantColor`); 3rd-party
// DSS OIs take purple.
const STATUS_STYLE_EXPR = (_kind: 'fill' | 'stroke') => [
  'case',
  ['get', 'stale'], VOLUME_PALETTE.gray,
  IS_PARTNER, VOLUME_PALETTE.partner,
  IS_THIRDPARTY, VOLUME_PALETTE.thirdparty,
  ['match',
    ['get', 'status'],
    // rtm_state statuses (ENG-213); Ended / Unknown fall through to gray.
    'Active', VOLUME_PALETTE.green,
    'Accepted', VOLUME_PALETTE.blue,
    'Scheduled', VOLUME_PALETTE.blue,
    'Contingent', VOLUME_PALETTE.amber,
    'Non-Conforming', VOLUME_PALETTE.red,
    'Rejected', VOLUME_PALETTE.red,
    VOLUME_PALETTE.gray,
  ],
];

export function ensureOiLayer(map: MapboxMap) {
  if (!map.getSource(SRC)) {
    map.addSource(SRC, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  }
  if (!map.getLayer(FILL_LAYER)) {
    map.addLayer({
      id: FILL_LAYER,
      type: 'fill',
      source: SRC,
      paint: {
        'fill-color': STATUS_STYLE_EXPR('fill') as never,
        'fill-opacity': [
          'case',
          IS_FUTURE, 0.04,
          ['get', 'stale'], 0.05,
          IS_PARTNER, 0.08,
          IS_THIRDPARTY, 0.08,
          ['match', ['get', 'status'],
            'Active', 0.10, 'Accepted', 0.10, 'Scheduled', 0.10, 'Contingent', 0.12,
            'Non-Conforming', 0.13, 'Rejected', 0.07, 0.07,
          ],
        ],
      },
    });
  }
  if (!map.getLayer(LINE_LAYER)) {
    map.addLayer({
      id: LINE_LAYER,
      type: 'line',
      source: SRC,
      paint: {
        'line-color': STATUS_STYLE_EXPR('stroke') as never,
        'line-width': ['case', ['get', 'focused'], VOLUME_LINE_WIDTH_FOCUSED, VOLUME_LINE_WIDTH],
        'line-opacity': ['case', IS_FUTURE, 0.5, 1],
        'line-dasharray': [
          'case',
          ['get', 'stale'], ['literal', [...VOLUME_DASH.loose]],
          IS_PARTNER, ['literal', [...VOLUME_DASH.partner]],
          IS_THIRDPARTY, ['literal', [...VOLUME_DASH.thirdparty]],
          ['match', ['get', 'status'],
            'Active', ['literal', [...VOLUME_DASH.solid]],
            'Accepted', ['literal', [...VOLUME_DASH.solid]],
            'Scheduled', ['literal', [...VOLUME_DASH.medium]],
            'Contingent', ['literal', [...VOLUME_DASH.medium]],
            'Non-Conforming', ['literal', [...VOLUME_DASH.tight]],
            'Rejected', ['literal', [...VOLUME_DASH.loose]],
            ['literal', [...VOLUME_DASH.loose]],
          ],
        ],
      },
    });
  }
}

export function syncOiLayer(map: MapboxMap) {
  // No isStyleLoaded gate — setData on a GeoJSON source works regardless
  // of basemap tile-load state. Source existence is the correct gate.
  // (Gating on isStyleLoaded paused sync for 30-60 s on slow staging
  // tile fetches; see surveillance.ts and the OI investigation doc.)
  const src = map.getSource(SRC) as GeoJSONSource | undefined;
  if (!src) return;
  const ui = useUiStore.getState();
  const ctx = ui.activeContext;
  if (ctx !== 'drones' && ctx !== 'oi') {
    src.setData({ type: 'FeatureCollection', features: [] });
    return;
  }
  if (ctx === 'drones' && !ui.showOIVolume) {
    src.setData({ type: 'FeatureCollection', features: [] });
    return;
  }
  const oiState = useOiStore.getState();
  const focusId = oiState.hoveredId ?? oiState.selectedId;
  const now = Date.now();
  const features: Feature<Polygon>[] = [];

  // Drone lookup keyed by drone_id — used for the `stale` flag (telemetry
  // lost). Polygon colour itself is the OI's rtm_state status (ENG-213).
  const dronesById = useDronesStore.getState().byId;
  // The same composed list the OI panel shows (ENG-313): REST (demo) +
  // FlightBlender schedule + per-drone MQTT, MQTT preferred.
  const oiList = composeOis({
    rest: queryClient.getQueryData<OiSummary[]>(['oi', 'list']) ?? [],
    blender: queryClient.getQueryData<OiSummary[]>(['oi', 'blender']),
    drones: Object.values(dronesById),
    now,
  });
  type DroneLookup = {
    lng: number;
    lat: number;
    predLng?: number;
    predLat?: number;
    staleness: 'live' | 'stale' | 'lost';
  };
  const droneByCallsign = new Map<string, DroneLookup>();
  for (const d of Object.values(dronesById)) {
    const p = d.telemetry.pred1min;
    droneByCallsign.set(d.telemetry.drone_id, {
      lng: d.telemetry.lng,
      lat: d.telemetry.lat,
      predLng: p?.lng,
      predLat: p?.lat,
      staleness: d.staleness,
    });
  }

  // The OI's status is its RTM `rtm_state` (ENG-213). When the drone's
  // telemetry is `lost` (no MQTT update > 10s, doc 007) flag the OI `stale`
  // so the polygon renders gray/dashed.
  const effectiveStatus = (oi: OiSummary): { status: OiSummary['status']; stale: boolean } => {
    const d = droneByCallsign.get(oi.aircraft);
    if (!d || !oi.polygon) return { status: oi.status, stale: false };
    if (d.staleness === 'lost') return { status: oi.status, stale: true };
    return { status: oi.status, stale: false };
  };

  // Filter chips key off the same rtm_state status the polygon is coloured
  // by, so the list and the map always hide the same OIs.
  const passesFilter = (status: OiSummary['status']): boolean =>
    oiState.filters[status as OiStatusFilter] !== false;

  if (ctx === 'oi') {
    // Render all OIs in the list that pass the active status filters and the
    // list's category row (All / Internal / Partner / 3rd Party) — same
    // buckets as OIList: anything not from a partner counts as internal.
    const cat = oiState.category;
    const showInternal = cat === 'all' || cat === 'internal';
    const showPartner = cat === 'all' || cat === 'partner';
    // 3rd Party polygons draw on 3rd Party and All; the search-area box and
    // the scrubber stay 3rd Party only.
    const show3rdParty = cat === '3rdparty' || cat === 'all';
    const passesCategory = (origin: string | undefined): boolean =>
      origin === 'partner' ? showPartner : showInternal;
    for (const oi of oiList) {
      const geom = oi.polygon ?? null;
      if (!geom) continue;
      if (!passesCategory(oi.origin)) continue;
      const { status, stale } = effectiveStatus(oi);
      if (!passesFilter(status)) continue;
      features.push({
        type: 'Feature',
        geometry: geom,
        properties: {
          status,
          stale,
          oi_id: oi.oi_id,
          focused: focusId === oi.oi_id,
          title: oi.title,
          aircraft: oi.aircraft,
          start_utc: oi.start_utc,
          end_utc: oi.end_utc,
          future: isFutureOi(oi.start_utc, now),
          partner: oi.origin === 'partner',
          partner_tenant: oi.partner_tenant ?? '',
        },
      });
    }
    // MQTT-only OIs are already in `oiList` (composeOis) — no per-drone fallback
    // here, or an OI the merge dropped as ended would be redrawn.
    // 3rd Party — DSS search results for the current search area, through the
    // same filter as the list (scrubber hour + search box). No status-chip
    // check: the DSS status vocabulary is its own.
    const dssOis = show3rdParty
      ? filterExternalOis(getCurrentExternalOis(), {
          hour: oiState.selectedHour,
          query: oiState.searchQuery,
          now,
        })
      : [];
    for (const oi of dssOis) {
      features.push({
        type: 'Feature',
        geometry: oi.polygon,
        properties: {
          status: oi.status,
          stale: false,
          oi_id: oi.oi_id,
          focused: focusId === oi.oi_id,
          title: oi.op_type,
          aircraft: oi.operator,
          start_utc: oi.start_utc,
          end_utc: oi.end_utc,
          partner: false,
          thirdparty: true,
          partner_tenant: '',
          future: isFutureOi(oi.start_utc, now),
        },
      });
    }
  } else {
    // Drones-ctx: render the OI assigned to each fleet drone. Fleet comes
    // from `visibleFleet()` so the fleet-list category filter hides a
    // partner drone's OI volume along with its marker — otherwise a
    // filtered-out aircraft keeps a polygon on the map with nothing in it.
    const oiByAircraft = new Map<string, OiSummary>();
    for (const oi of oiList) oiByAircraft.set(oi.aircraft, oi);
    const drones = visibleFleet();
    for (const d of drones) {
      const oi = oiByAircraft.get(d.telemetry.drone_id);
      // The composed list already carries each drone's MQTT OI (with its
      // polygon). No polygon ⇒ no polygon: we used to paint a synthetic box
      // here, which misled operators into reading a placeholder as a real OI.
      if (!oi?.polygon) continue;
      const geom = oi.polygon;
      const oi_id = oi.oi_id;
      const { status, stale } = effectiveStatus(oi);
      // Apply OI status filter chips here too — otherwise the OI list
      // hides the row but the polygon stays on the map (mockup-divergence
      // bug: filter felt "broken" in Drones context).
      if (!passesFilter(status)) continue;
      features.push({
        type: 'Feature',
        geometry: geom,
        properties: {
          status,
          stale,
          oi_id,
          focused: focusId != null && oi_id === focusId,
          title: oi.title,
          aircraft: oi.aircraft,
          start_utc: oi.start_utc,
          end_utc: oi.end_utc,
          partner: d.origin === 'partner',
          partner_tenant: d.partnerTenant ?? '',
        },
      });
    }
    // Scheduled / drone-less OIs (ENG-313) have no fleet drone to hang off, so
    // the loop above never reaches them — draw them here, same filters.
    for (const oi of oiList) {
      if (oi.aircraft || !oi.polygon || !passesFilter(oi.status)) continue;
      features.push({
        type: 'Feature',
        geometry: oi.polygon,
        properties: {
          status: oi.status,
          stale: false,
          oi_id: oi.oi_id,
          focused: focusId != null && oi.oi_id === focusId,
          title: oi.title,
          aircraft: '',
          start_utc: oi.start_utc,
          end_utc: oi.end_utc,
          partner: false,
          partner_tenant: '',
        },
      });
    }
  }

  src.setData({ type: 'FeatureCollection', features });
}

// Mockup `STATUS_STYLE` L9446-9454 / `oiStatus` chip — we surface the same
// status colour in the click-popup chip so the polygon stroke colour reads
// straight through to the legend.
const STATUS_CHIP_COLOR: Record<string, string> = {
  Active: VOLUME_PALETTE.green,
  Accepted: VOLUME_PALETTE.blue,
  Scheduled: VOLUME_PALETTE.blue,
  Contingent: VOLUME_PALETTE.amber,
  'Non-Conforming': VOLUME_PALETTE.red,
  Rejected: VOLUME_PALETTE.red,
  Ended: VOLUME_PALETTE.gray,
  Unknown: VOLUME_PALETTE.gray,
};

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}

function trimUtc(iso: string): string {
  // Mockup strips the `2026-04-09T` date prefix and ` UTC` suffix for compact
  // popup display (L9578-9579). We do the same when we have a valid ISO; bail
  // gracefully on partial strings.
  if (!iso) return '';
  const m = iso.match(/T(\d{2}:\d{2})/);
  return m && m[1] ? m[1] : iso;
}

function buildOiPopupHtml(props: Record<string, unknown>): string {
  const oiId = String(props.oi_id ?? '—');
  const title = String(props.title ?? '');
  const aircraft = String(props.aircraft ?? '—');
  const status = String(props.status ?? '—');
  const startUtc = String(props.start_utc ?? '');
  const endUtc = String(props.end_utc ?? '');
  const isThirdParty = props.thirdparty === true;
  const statusBg = isThirdParty ? '#7c3aed' : (STATUS_CHIP_COLOR[status] ?? '#6b7280');
  // Mockup external-polygon tooltips: "Partner · {tenant}" / "3rd Party
  // (DSS)" tag an OI that isn't ours. Internal OIs show the status chip
  // alone — the single RTM status (ENG-213).
  const isPartner = props.partner === true;
  const partnerTenant = escapeHtml(String(props.partner_tenant || '—'));
  const tagStyle =
    'font-size:0.615em;font-weight:700;color:#7c3aed;background:#7c3aed20;border:1px solid #7c3aed40;border-radius:3px;padding:0 6px';
  const secondChip = isThirdParty
    ? `<span style="${tagStyle}">3rd Party (DSS)</span>`
    : isPartner
      ? `<span style="${tagStyle}">Partner · ${partnerTenant}</span>`
      : '';
  const dateRow = startUtc && endUtc
    ? `<div style="font-size:0.692em;color:#777;margin-bottom:5px;` +
      `font-family:IBM Plex Mono,monospace">${escapeHtml(trimUtc(startUtc))} – ` +
      `${escapeHtml(trimUtc(endUtc))} UTC</div>`
    : '';
  return (
    `<div style="font-family:Inter,sans-serif;min-width:170px">` +
      `<div style="font-weight:700;font-size:0.846em;margin-bottom:1px">${escapeHtml(oiId)}</div>` +
      `<div style="font-size:0.692em;color:#555;margin-bottom:5px">` +
        `${escapeHtml(title)} · ${escapeHtml(aircraft)}</div>` +
      dateRow +
      `<div style="display:flex;gap:5px;align-items:center;flex-wrap:wrap">` +
        `<span style="font-size:0.615em;font-weight:700;color:#fff;background:${statusBg};` +
        `border-radius:3px;padding:1px 6px">${escapeHtml(status)}</span>` +
        secondChip +
      `</div>` +
    `</div>`
  );
}

export function subscribeOiLayer(map: MapboxMap): () => void {
  ensureOiLayer(map);
  let rafId: number | null = null;
  // No `idle` retry. Each `setData` causes Mapbox to repaint, which
  // triggers another `idle` — wiring sync to idle would recurse at the
  // browser's frame rate. Source-readiness is handled by `attachCore`
  // running on `style.load`; every other retrigger comes from store
  // subscriptions below.
  syncOiLayer(map);
  const schedule = () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => { rafId = null; syncOiLayer(map); });
  };
  const unsubD = useDronesStore.subscribe((s) => s.byId, schedule);
  const unsubUi = useUiStore.subscribe(
    (s) => `${droneFilterKey(s)}-${s.showOIVolume}`,
    schedule,
  );
  const unsubOi = useOiStore.subscribe(
    (s) => `${s.hoveredId}|${s.selectedId}|${s.category}|${s.selectedHour}|${s.searchQuery}|${JSON.stringify(s.filters)}`,
    schedule,
  );
  // Future OIs un-fade when their window opens.
  const minuteTick = window.setInterval(schedule, 60_000);
  // Re-sync when the OI list query lands so polygons take over from the
  // boxAround fallback.
  const qUnsub = queryClient.getQueryCache().subscribe((event) => {
    if (event.query.queryKey[0] === 'oi') schedule();
  });

  // Mockup `poly.bindPopup` (L9519, L9573) — click an OI polygon to open a
  // detail popup. We use one shared mapboxgl.Popup that re-opens at the click
  // point with the clicked feature's properties.
  const popup = new mapboxgl.Popup({
    closeButton: true,
    closeOnClick: true,
    maxWidth: '230px',
    offset: 12,
  });
  const onClick = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }) => {
    // Dropping the search-area box on a polygon must not select that OI.
    if (searchAreaDragJustEnded()) return;
    const f = e.features?.[0];
    if (!f) return;
    popup
      .setLngLat(e.lngLat)
      .setHTML(buildOiPopupHtml((f.properties ?? {}) as Record<string, unknown>))
      .addTo(map);
    // Sync hovered/selected state so the polygon's focused-stroke flips on.
    const oiId = (f.properties as { oi_id?: string } | null)?.oi_id;
    if (oiId) useOiStore.getState().select(oiId);
  };
  const onMouseEnter = () => { map.getCanvas().style.cursor = 'pointer'; };
  const onMouseLeave = () => { map.getCanvas().style.cursor = ''; };
  map.on('click', FILL_LAYER, onClick);
  map.on('mouseenter', FILL_LAYER, onMouseEnter);
  map.on('mouseleave', FILL_LAYER, onMouseLeave);

  // Mockup `_focusOIPolygon` L10720+ — when the user opens an OI in the LP
  // detail panel (selectedId change, either from list click or polygon click),
  // zoom the map to the polygon's bounding box.
  let fitId: number | null = null;
  const fitToSelected = (oiId: string | null) => {
    if (!oiId) return;
    if (!map.isStyleLoaded()) {
      map.once('idle', () => fitToSelected(oiId));
      return;
    }
    const oiList = queryClient.getQueryData<OiSummary[]>(['oi', 'list']) ?? [];
    const oi = oiList.find((x) => x.oi_id === oiId);
    // MQTT-only OIs (partner OIs, live mode) aren't in the REST cache, and
    // DSS 3rd-party OIs live in their own query.
    const ring =
      oi?.polygon?.coordinates?.[0] ??
      Object.values(useDronesStore.getState().byId).find((d) => d.oi?.oi_id === oiId)?.oi
        ?.polygon?.coordinates?.[0] ??
      getCurrentExternalOis().find((x) => x.oi_id === oiId)?.polygon.coordinates[0];
    if (!ring || ring.length < 3) return;
    let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
    for (const [lng, lat] of ring) {
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }
    if (!Number.isFinite(minLng)) return;
    map.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 80, maxZoom: 14, duration: 600 });
  };
  const unsubSel = useOiStore.subscribe(
    (s) => s.selectedId,
    (selId) => {
      if (fitId !== null) clearTimeout(fitId);
      fitId = window.setTimeout(() => { fitId = null; fitToSelected(selId); }, 80);
    },
  );

  return () => {
    unsubD();
    unsubUi();
    unsubOi();
    unsubSel();
    qUnsub();
    if (fitId !== null) clearTimeout(fitId);
    window.clearInterval(minuteTick);
    map.off('click', FILL_LAYER, onClick);
    map.off('mouseenter', FILL_LAYER, onMouseEnter);
    map.off('mouseleave', FILL_LAYER, onMouseLeave);
    popup.remove();
  };
}
